import { useCallback, useMemo, memo } from 'react';
import { cn } from '@divlab/divanui';
import {
  IconUser,
  IconCatalog,
  IconBasket,
  IconShowRoom,
  IconFavorites,
} from '@divlab/divanui/icons';

import * as Compare from '@Queries/Compare';
import * as Profile from '@Queries/Profile';
import * as ModalWindows from '@Stores/ModalWindows';
import * as HeaderStore from '@Stores/Header';
import * as Cart from '@Queries/Cart';
import * as Meta from '@Queries/Meta';
import { useFeatureFlags } from '@Contexts/FeatureFlags';
import { useRequest } from '@Contexts/Request';
import useRouteMatchers from '@Navigation/useRouteMatchers';
import useTranslation from '@Queries/useTranslation';
import Link from '@Navigation/Link';
import styles from './BottomUserMenu.module.css';

import type { SellPointData } from '@Types/SellPoints';
import type { FC, HTMLAttributes } from 'react';

export interface BottomMenuProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  propSellPoint: SellPointData[];
}

const BottomUserMenu: FC<BottomMenuProps> = (props) => {
  const { className, propSellPoint, ...restProps } = props;
  const { t } = useTranslation();
  const cart = Cart.useCartShortInfo();
  const profile = Profile.useProfile();
  const { data: meta } = Meta.useMeta();
  const { country } = useRequest();
  const featureFlags = useFeatureFlags();
  const needIconUser = featureFlags.personalAreaAvailable;
  const { isOrderCheck, isShowroomPage, isFavourites, isCabinet } = useRouteMatchers();
  const { compareCount } = Compare.useCompare();

  const hasSellPoints = useMemo(() => {
    if (country === 'BLR') return true;
    return !!propSellPoint?.some((item) => meta.region.id === item.regionId);
  }, [meta, propSellPoint, country]);

  const cartCount = useMemo(() => {
    return cart.data?.count || 0;
  }, [cart]);

  const handleClickAuthorization = useCallback(() => {
    ModalWindows.open('Authorization');
  }, []);

  const handleClickCatalog = useCallback(() => {
    HeaderStore.preselectSideBarItem();
    HeaderStore.openSideBar();
  }, []);

  return (
    <div {...restProps} className={cn(styles.wrapper, className)}>
      <div className={styles.fixed}>
        <div className={styles.list}>
          <div className={styles.item} onClick={handleClickCatalog} data-testid='catalog-below'>
            <IconCatalog className={styles.icon} />
            <div>{t('ui.bottom.catalog')}</div>
          </div>

          <Link className={styles.item} to='/favorites' view='secondary' data-testid='favorites'>
            <div className={styles.iconWrapper}>
              <IconFavorites className={styles.icon} full={isFavourites} />
              {!!compareCount && (
                <span className={cn(styles.count, { [styles.full]: isFavourites })}>
                  {compareCount}
                </span>
              )}
            </div>
            <div>{t('ui.bottom.favourites')}</div>
          </Link>

          <Link className={styles.item} view='secondary' to='/order/check' data-testid='order'>
            <div className={styles.iconWrapper}>
              <IconBasket className={styles.iconBasket} full={isOrderCheck} />
              {!!cartCount && (
                <span className={cn(styles.count, { [styles.full]: isOrderCheck })}>
                  {cartCount}
                </span>
              )}
            </div>
            <div>{t('ui.bottom.cart')}</div>
          </Link>

          {needIconUser && (
            <>
              {profile.data ? (
                <Link
                  to='/cabinet'
                  className={styles.item}
                  view='secondary'
                  data-testid='authorized-user'
                >
                  <IconUser className={styles.icon} full={isCabinet} />
                  <div>{t('ui.bottom.profile')}</div>
                </Link>
              ) : (
                <div
                  className={styles.item}
                  onClick={handleClickAuthorization}
                  data-testid='unauthorized-user'
                >
                  <IconUser className={styles.icon} full={isCabinet} />
                  <div>{t('ui.bottom.sign-in')}</div>
                </div>
              )}
            </>
          )}

          {hasSellPoints && (
            <Link
              to='/site/showroom'
              className={styles.item}
              view='secondary'
              data-testid='showroom-mobile'
            >
              <div className={styles.iconWrapper}>
                <IconShowRoom className={styles.iconShowroom} full={isShowroomPage} />
              </div>
              <div>{t('ui.bottom.showrooms')}</div>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(BottomUserMenu);
